/**
 * Google Cloud TTS voice 목록 (Standard)
 * @see https://cloud.google.com/text-to-speech/docs/voices
 *
 * lanType 은 Google TTS 의 languageCode(BCP-47) 와 동일하게 사용한다.
 * 언어를 추가할 때는 TTS_VOICE_DEFINITIONS 에 voice 를 추가하고,
 * _constants.ts 의 TTS_LANGUAGES 에 동일한 lanCd 를 추가하면 된다.
 */

// Google Cloud TTS languageCode (예: 'ko-KR'). 언어 선택값(lanCd)과 동일하다.
export type TtsLangType = string;

export type TtsPauseLevel = 'none' | 'short' | 'medium' | 'long';

export type TtsVoiceDefinition = {
  voiceId: string;
  cdName: string;
  languageCode: string;
  ssmlGender: 'MALE' | 'FEMALE';
  lanType: TtsLangType;
};

export type TtsVoiceSelectOption = TtsVoiceDefinition & { type: 'voiceId' };

export const TTS_SPEAKING_RATE_OPTIONS = [
  { type: 'speakingRate', speakingRate: '0.75', cdName: '0.75x (느리게)' },
  { type: 'speakingRate', speakingRate: '0.85', cdName: '0.85x' },
  { type: 'speakingRate', speakingRate: '1', cdName: '1.0x (기본)' },
  { type: 'speakingRate', speakingRate: '1.1', cdName: '1.1x' },
  { type: 'speakingRate', speakingRate: '1.25', cdName: '1.25x (빠르게)' },
];

export const TTS_PAUSE_OPTIONS = [
  { type: 'pauseLevel', pauseLevel: 'none' as TtsPauseLevel, cdName: '없음' },
  { type: 'pauseLevel', pauseLevel: 'short' as TtsPauseLevel, cdName: '짧게' },
  { type: 'pauseLevel', pauseLevel: 'medium' as TtsPauseLevel, cdName: '보통' },
  { type: 'pauseLevel', pauseLevel: 'long' as TtsPauseLevel, cdName: '길게' },
];

const TTS_VOICE_DEFINITIONS: TtsVoiceDefinition[] = [
  // 국내 건설현장 외국인 근로자 주요 언어 (Google Cloud TTS Standard)
  { voiceId: 'ko-KR-Standard-A', cdName: '여성 A', languageCode: 'ko-KR', ssmlGender: 'FEMALE', lanType: 'ko-KR' },
  { voiceId: 'ko-KR-Standard-C', cdName: '남성 C', languageCode: 'ko-KR', ssmlGender: 'MALE', lanType: 'ko-KR' },
  { voiceId: 'cmn-CN-Standard-A', cdName: '여성 A', languageCode: 'cmn-CN', ssmlGender: 'FEMALE', lanType: 'cmn-CN' },
  { voiceId: 'cmn-CN-Standard-B', cdName: '남성 B', languageCode: 'cmn-CN', ssmlGender: 'MALE', lanType: 'cmn-CN' },
  { voiceId: 'vi-VN-Standard-A', cdName: '여성 A', languageCode: 'vi-VN', ssmlGender: 'FEMALE', lanType: 'vi-VN' },
  { voiceId: 'vi-VN-Standard-B', cdName: '남성 B', languageCode: 'vi-VN', ssmlGender: 'MALE', lanType: 'vi-VN' },
  { voiceId: 'th-TH-Standard-A', cdName: '여성 A', languageCode: 'th-TH', ssmlGender: 'FEMALE', lanType: 'th-TH' },
  { voiceId: 'ru-RU-Standard-A', cdName: '여성 A', languageCode: 'ru-RU', ssmlGender: 'FEMALE', lanType: 'ru-RU' },
  { voiceId: 'ru-RU-Standard-B', cdName: '남성 B', languageCode: 'ru-RU', ssmlGender: 'MALE', lanType: 'ru-RU' },
  { voiceId: 'id-ID-Standard-A', cdName: '여성 A', languageCode: 'id-ID', ssmlGender: 'FEMALE', lanType: 'id-ID' },
  { voiceId: 'id-ID-Standard-B', cdName: '남성 B', languageCode: 'id-ID', ssmlGender: 'MALE', lanType: 'id-ID' },
  { voiceId: 'ms-MY-Standard-A', cdName: '여성 A', languageCode: 'ms-MY', ssmlGender: 'FEMALE', lanType: 'ms-MY' },
  { voiceId: 'ms-MY-Standard-B', cdName: '남성 B', languageCode: 'ms-MY', ssmlGender: 'MALE', lanType: 'ms-MY' },
  { voiceId: 'fil-PH-Standard-A', cdName: '여성 A', languageCode: 'fil-PH', ssmlGender: 'FEMALE', lanType: 'fil-PH' },
  { voiceId: 'fil-PH-Standard-C', cdName: '남성 C', languageCode: 'fil-PH', ssmlGender: 'MALE', lanType: 'fil-PH' },
  { voiceId: 'bn-IN-Standard-A', cdName: '여성 A', languageCode: 'bn-IN', ssmlGender: 'FEMALE', lanType: 'bn-IN' },
  { voiceId: 'bn-IN-Standard-B', cdName: '남성 B', languageCode: 'bn-IN', ssmlGender: 'MALE', lanType: 'bn-IN' },
  { voiceId: 'ur-IN-Standard-A', cdName: '여성 A', languageCode: 'ur-IN', ssmlGender: 'FEMALE', lanType: 'ur-IN' },
  { voiceId: 'ur-IN-Standard-B', cdName: '남성 B', languageCode: 'ur-IN', ssmlGender: 'MALE', lanType: 'ur-IN' },
  { voiceId: 'hi-IN-Standard-A', cdName: '여성 A', languageCode: 'hi-IN', ssmlGender: 'FEMALE', lanType: 'hi-IN' },
  { voiceId: 'hi-IN-Standard-B', cdName: '남성 B', languageCode: 'hi-IN', ssmlGender: 'MALE', lanType: 'hi-IN' },
  { voiceId: 'en-US-Standard-C', cdName: '여성 C', languageCode: 'en-US', ssmlGender: 'FEMALE', lanType: 'en-US' },
  { voiceId: 'en-US-Standard-A', cdName: '남성 A', languageCode: 'en-US', ssmlGender: 'MALE', lanType: 'en-US' },
];

const DEFAULT_VOICE: TtsVoiceSelectOption = {
  type: 'voiceId',
  voiceId: 'ko-KR-Standard-A',
  cdName: '여성 A',
  languageCode: 'ko-KR',
  ssmlGender: 'FEMALE',
  lanType: 'ko-KR',
};

// lanType 은 languageCode 와 동일하므로 그대로 반환한다. (이전 호출부 호환용으로 함수 유지)
export const lanCdToTtsType = (lanCd: string): TtsLangType => lanCd;

export const getVoiceSelectOptions = (lanType: TtsLangType): TtsVoiceSelectOption[] => {
  return TTS_VOICE_DEFINITIONS.filter((v) => v.lanType === lanType).map((v) => ({ type: 'voiceId', ...v }));
};

export const getDefaultVoiceOption = (lanType: TtsLangType): TtsVoiceSelectOption => {
  const options = getVoiceSelectOptions(lanType);
  return options[0] ?? DEFAULT_VOICE;
};

export const findVoiceOption = (lanType: TtsLangType, voiceId: string): TtsVoiceSelectOption | undefined => {
  return getVoiceSelectOptions(lanType).find((v) => v.voiceId === voiceId);
};
